import React, { useState, useMemo, useEffect } from 'react';
import Select from 'react-select';
import { Search, X, ChevronRight, ChevronLeft, Building2, FileText } from 'lucide-react';
import useAppStore from '../store/appStore';
import { getAgencyConfig } from '../config/agency';
// import VulcanConsolidationInsights from './VulcanConsolidationInsights'; // Removed

const Sidebar = () => {
  const {
    sidebarCollapsed,
    toggleSidebar,
    agencies,
    selectedAgencies,
    toggleAgency,
    clearSelectedAgencies,
    searchQuery,
    setSearchQuery,
    getFilteredRegulations,
    selectRegulation,
    selectedRegulations,
    activeRegulationId,
    loadingAgencies,
    loadingRegulations,
    selectedStatutes,
    toggleStatute,
    clearSelectedStatutes,
    getUniqueStatutes,
    setSelectedAgencies,
    regulations,  // Add this to fix the ReferenceError
    consolidationMode,
    selectedGroup,
    regulationGroups,
    setSelectedGroup,
    clearSelectedRegulations,
    groupsLoading
  } = useAppStore();

  const [sortBy, setSortBy] = useState('number'); // 'number', 'discretionary', or 'statute'
  const agencyConfig = getAgencyConfig();

  // Handle agency locking - auto-select locked agency
  useEffect(() => {
    if (agencyConfig.locked && agencyConfig.agency && agencies.length > 0) {
      console.log('🔒 Agency locked mode detected:', agencyConfig.agency);
      console.log('🔒 Available agencies:', agencies.map(a => a.id));
      console.log('🔒 Currently selected agencies:', selectedAgencies);
      
      // Always ensure the locked agency is selected
      if (!selectedAgencies.includes(agencyConfig.agency)) {
        console.log('🔒 Auto-selecting locked agency:', agencyConfig.agency);
        // Make sure the agency exists before selecting
        const agencyExists = agencies.some(a => a.id === agencyConfig.agency);
        if (agencyExists) {
          // Clear other selections and select only the locked agency
          setSelectedAgencies([agencyConfig.agency]);
          console.log('🔒 Selected agencies set to:', [agencyConfig.agency]);
        } else {
          console.error('🔒 Locked agency not found in available agencies:', agencyConfig.agency);
          // Try adding it anyway if we're in locked mode
          setSelectedAgencies([agencyConfig.agency]);
        }
      }
    }
  }, [agencyConfig.locked, agencyConfig.agency, agencies.length]); // Remove selectedAgencies.length from dependencies
  
  // Log component updates
  useEffect(() => {
    console.log('%c📋 [Sidebar] Component updated', 'color: #9C27B0; font-weight: bold', {
      agencies: agencies.length,
      selectedAgencies,
      searchQuery,
      collapsed: sidebarCollapsed,
      agencyLocked: agencyConfig.locked,
      lockedAgency: agencyConfig.agency
    });
  }, [selectedAgencies, searchQuery, sidebarCollapsed]);

  const [agencySearchFocused, setAgencySearchFocused] = useState(false);
  const [showStatuteFilter, setShowStatuteFilter] = useState(false);

  const filteredRegulations = useMemo(() => {
    const filtered = getFilteredRegulations();
    
    // Sort regulations based on selected sort option
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'discretionary') {
        // Sort by discretionary requirements count (highest first)
        const aCount = a.discretionary_requirements_total || 0;
        const bCount = b.discretionary_requirements_total || 0;
        if (bCount !== aCount) return bCount - aCount;
        // If equal, fall back to regulation number
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'statute') {
        // Sort by authorizing statute
        const aStatute = a.authorizingStatute || 'ZZZ'; // Put empty statutes at the end
        const bStatute = b.authorizingStatute || 'ZZZ';
        if (aStatute !== bStatute) return aStatute.localeCompare(bStatute);
        // If equal, fall back to regulation number
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'mandatory') {
        // Sort by mandatory count (highest first)
        const aCount = a.mandatory_count || 0;
        const bCount = b.mandatory_count || 0;
        if (bCount !== aCount) return bCount - aCount;
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'authorized') {
        // Sort by authorized count (highest first)
        const aCount = a.authorized_count || 0;
        const bCount = b.authorized_count || 0;
        if (bCount !== aCount) return bCount - aCount;
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'unauthorized') {
        // Sort by unauthorized count (highest first)
        const aCount = a.unauthorized_count || 0;
        const bCount = b.unauthorized_count || 0;
        if (bCount !== aCount) return bCount - aCount;
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'total_provisions') {
        // Sort by total provisions (highest first)
        const aCount = a.total_provisions || 0;
        const bCount = b.total_provisions || 0;
        if (bCount !== aCount) return bCount - aCount;
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'loper_bright') {
        // Sort by Loper Bright violations (highest first)
        const aCount = a.loper_bright_violations || 0;
        const bCount = b.loper_bright_violations || 0;
        if (bCount !== aCount) return bCount - aCount;
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'sackett') {
        // Sort by Sackett violations (highest first)
        const aCount = a.sackett_violations || 0;
        const bCount = b.sackett_violations || 0;
        if (bCount !== aCount) return bCount - aCount;
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'west_virginia') {
        // Sort by West Virginia violations (highest first)
        const aCount = a.west_virginia_violations || 0;
        const bCount = b.west_virginia_violations || 0;
        if (bCount !== aCount) return bCount - aCount;
        return a.number.localeCompare(b.number);
      } else if (sortBy === 'total_scotus') {
        // Sort by total SCOTUS violations (highest first)
        const aCount = a.total_scotus_violations || 0;
        const bCount = b.total_scotus_violations || 0;
        if (bCount !== aCount) return bCount - aCount;
        return a.number.localeCompare(b.number);
      } else {
        // Default sort by regulation number
        return a.number.localeCompare(b.number);
      }
    });
    
    console.log('%c🎯 [Sidebar] Regulations filtered:', 'color: #673AB7', {
      totalRegulations: sorted.length,
      selectedAgencies,
      searchQuery,
      sortBy,
      hasFilter: selectedAgencies.length > 0 || searchQuery.length > 0
    });
    return sorted;
  }, [
    selectedAgencies,
    searchQuery,
    getFilteredRegulations,
    sortBy,
    regulations,  // Add regulations to trigger re-calculation when they load
  ]);

  const agencyOptions = useMemo(() => 
    agencies.map(agency => ({
      value: agency.id,
      label: agency.name,
      acronym: agency.acronym,
    })),
    [agencies]
  );

  const selectedAgencyOptions = useMemo(() => 
    agencyOptions.filter(option => selectedAgencies.includes(option.value)),
    [agencyOptions, selectedAgencies]
  );

  const handleAgencyChange = (selectedOptions) => {
    console.log('%c🏢 [Sidebar] Agency selection changed:', 'color: #9C27B0; font-weight: bold', {
      previous: selectedAgencies,
      new: selectedOptions?.map(o => o.value) || [],
      options: selectedOptions
    });
    clearSelectedAgencies();
    if (selectedOptions && selectedOptions.length > 0) {
      selectedOptions.forEach(option => {
        console.log('   ➤ Selecting agency:', {
          id: option.value,
          name: option.label,
          acronym: option.acronym
        });
        toggleAgency(option.value);
      });
    } else {
      console.log('%c🛝 [Sidebar] All agencies cleared', 'color: #FF5722');
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderColor: 'var(--border)',
      backgroundColor: 'var(--bg-primary)',
      fontSize: '13px',
      minHeight: '38px',
      '&:hover': {
        borderColor: 'var(--accent)',
      },
      boxShadow: 'none',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#eff6ff',
      borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#2563eb',
      fontSize: '12px',
      fontWeight: '500',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#2563eb',
      '&:hover': {
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
      },
    }),
    menu: (base) => ({
      ...base,
      fontSize: '13px',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '13px',
      backgroundColor: state.isSelected ? '#eff6ff' : state.isFocused ? '#f8f8f8' : 'white',
      color: state.isSelected ? '#2563eb' : '#0f0f0f',
    }),
  };

  if (sidebarCollapsed) {
    return (
      <div className="sidebar collapsed">
        <div style={{ padding: '20px 10px', textAlign: 'center' }}>
          <button
            onClick={toggleSidebar}
            className="btn btn-secondary"
            style={{ 
              padding: '8px', 
              marginBottom: '20px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            title="Expand sidebar"
          >
            <ChevronRight size={18} />
          </button>
          <Building2 size={20} style={{ margin: '0 auto 16px', color: 'var(--text-secondary)' }} />
          <Search size={20} style={{ margin: '0 auto 16px', color: 'var(--text-secondary)' }} />
          <FileText size={20} style={{ margin: '0 auto', color: 'var(--text-secondary)' }} />
        </div>
      </div>
    );
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', position: 'relative', zIndex: 30 }}>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('%c🔘 [Sidebar] Collapse button clicked', 'color: #FF5722; font-weight: bold');
              toggleSidebar();
            }}
            className="btn btn-secondary"
            style={{ 
              padding: '6px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              cursor: 'pointer',
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 100,
              border: '1px solid var(--border)'
            }}
            title="Collapse sidebar"
            type="button"
          >
            <ChevronLeft size={16} />
            <span>Collapse</span>
          </button>
        </div>
        {!agencyConfig.locked ? (
          <div className="filter-section">
            <label className="filter-label">Select Agencies</label>
            <Select
              isMulti
              value={selectedAgencyOptions}
              onChange={handleAgencyChange}
              options={agencyOptions}
              styles={customSelectStyles}
              placeholder="Search and select agencies..."
              noOptionsMessage={() => "No agencies found"}
              isLoading={loadingAgencies}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        ) : (
          <div className="filter-section">
            <label className="filter-label">Agency</label>
            <div style={{
              padding: '10px',
              background: 'rgba(37, 99, 235, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(37, 99, 235, 0.2)'
            }}>
              <Building2 className="inline-icon" style={{ marginRight: '8px' }} />
              {agencyConfig.displayName}
            </div>
          </div>
        )}


        {/* Only show search and regulation list if not in consolidation mode */}
        {!consolidationMode && (
        <>
        {/* Clear Selected Regulations Button */}
        {selectedRegulations.length > 1 && (
          <button
            onClick={clearSelectedRegulations}
            style={{
              width: '100%',
              padding: '8px',
              marginBottom: '12px',
              fontSize: '13px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <X size={16} />
            Clear {selectedRegulations.length} Selected Regulations
          </button>
        )}
        <div className="search-box">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              const newQuery = e.target.value;
              console.log('%c🔍 [Sidebar] Search query changed:', 'color: #2196F3', {
                previous: searchQuery,
                new: newQuery,
                length: newQuery.length
              });
              setSearchQuery(newQuery);
            }}
            onFocus={() => console.log('%c🔍 [Sidebar] Search input focused', 'color: #2196F3')}
            onBlur={() => console.log('%c🔍 [Sidebar] Search input blurred', 'color: #2196F3')}
            placeholder="Search regulations..."
            className="search-input"
          />
          {searchQuery && (
            <button
              onClick={() => {
                console.log('%c❌ [Sidebar] Clear search clicked', 'color: #F44336', { previousQuery: searchQuery });
                setSearchQuery('');
              }}
              style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X size={16} style={{ color: 'var(--text-tertiary)' }} />
            </button>
          )}
        </div>
        
        <div className="filter-section">
          <label className="filter-label">Sort By</label>
          <Select
            value={{ 
              value: sortBy, 
              label: sortBy === 'number' ? 'Regulation Number' : 
                     sortBy === 'discretionary' ? 'Discretionary Requirements' : 
                     sortBy === 'statute' ? 'Authorizing Statute' :
                     sortBy === 'mandatory' ? 'Mandatory Count (Green)' :
                     sortBy === 'authorized' ? 'Authorized Count (Yellow)' :
                     sortBy === 'unauthorized' ? 'Unauthorized Count (Red)' :
                     sortBy === 'total_provisions' ? 'Total Provisions' :
                     sortBy === 'loper_bright' ? 'Loper Bright Violations' :
                     sortBy === 'sackett' ? 'Sackett Violations' :
                     sortBy === 'west_virginia' ? 'West Virginia Violations' :
                     sortBy === 'total_scotus' ? 'Total SCOTUS Violations' : 'Regulation Number'
            }}
            onChange={(option) => setSortBy(option.value)}
            options={[
              { value: 'number', label: 'Regulation Number' },
              { value: 'discretionary', label: 'Discretionary Requirements' },
              { value: 'statute', label: 'Authorizing Statute' },
              { value: 'mandatory', label: 'Mandatory Count (Green)' },
              { value: 'authorized', label: 'Authorized Count (Yellow)' },
              { value: 'unauthorized', label: 'Unauthorized Count (Red)' },
              { value: 'total_provisions', label: 'Total Provisions' },
              { value: 'loper_bright', label: 'Loper Bright Violations' },
              { value: 'sackett', label: 'Sackett Violations' },
              { value: 'west_virginia', label: 'West Virginia Violations' },
              { value: 'total_scotus', label: 'Total SCOTUS Violations' }
            ]}
            styles={customSelectStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
        </>
        )} {/* Close the !consolidationMode conditional */}
      </div> {/* Close sidebar-header */}
      
      {/* Scrollable content area - used for both regular and consolidation mode */}
      <div className="sidebar-content">
        {!consolidationMode ? (
          // Regular regulation list
          loadingRegulations ? (
            <div className="loading">
              <div className="spinner" />
            </div>
          ) : filteredRegulations.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px' }}>
              <p className="text-small" style={{ textAlign: 'center' }}>
                {selectedAgencies.length === 0 && !agencyConfig.locked
                  ? "Select agencies to view regulations"
                  : "No regulations found matching your criteria"}
              </p>
            </div>
          ) : (
            <div className="regulation-list">
              {filteredRegulations.filter(regulation => regulation && regulation.id).map((regulation) => {
                const isSelected = selectedRegulations.includes(regulation.id);
                const isActive = activeRegulationId === regulation.id;

                return (
                  <div
                    key={regulation.id}
                    className={`regulation-item ${isSelected ? 'selected' : ''} ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      console.log('%c📜 [Sidebar] Regulation clicked:', 'color: #4CAF50; font-weight: bold', {
                        id: regulation.id,
                        number: regulation.number,
                        title: regulation.title,
                        agency: regulation.agencyId,
                        isSelected,
                        isActive
                      });
                      selectRegulation(regulation.id);
                    }}
                  >
                    <div className="regulation-number">
                      {regulation.number.replace(/(\d+)_cfr_(\d+)/, 'CFR $1.$2')}
                    </div>
                    <div className="regulation-title">{regulation.title}</div>
                    <div className="regulation-meta">
                      {/* Authorizing statute - limited to 2 lines */}
                      <div style={{ width: '100%', marginBottom: '2px' }}>
                        <div className="statute-badge">
                          {(() => {
                            const text = regulation.authorizingStatute || '';
                            // Truncate to approximately 2 lines worth of characters
                            if (text.length > 70) {
                              // Trim any trailing spaces before adding ellipsis
                              return text.substring(0, 70).trimEnd() + '...';
                            }
                            return text;
                          })()}
                        </div>
                      </div>
                      
                      {/* SCOTUS violations - own line */}
                      {(regulation.loper_bright_violations > 0 || 
                        regulation.sackett_violations > 0 || 
                        regulation.west_virginia_violations > 0) && (
                        <div style={{ 
                          width: '100%',
                          display: 'flex', 
                          gap: '4px', 
                          marginBottom: '4px'
                        }}>
                          {regulation.loper_bright_violations > 0 && (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: '#7c3aed',
                              color: 'white'
                            }} title="Loper Bright violations">
                              LB: {regulation.loper_bright_violations}
                            </span>
                          )}
                          {regulation.sackett_violations > 0 && (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: '#dc2626',
                              color: 'white'
                            }} title="Sackett violations">
                              S: {regulation.sackett_violations}
                            </span>
                          )}
                          {regulation.west_virginia_violations > 0 && (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: '#ea580c',
                              color: 'white'
                            }} title="West Virginia violations">
                              WV: {regulation.west_virginia_violations}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Authorization tallies and discretionary requirements */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {regulation.mandatory_count > 0 && (
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: '#10b981',
                            color: 'white'
                          }} title="Mandatory provisions">
                            {regulation.mandatory_count}
                          </span>
                        )}
                        {regulation.authorized_count > 0 && (
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: '#f59e0b',
                            color: 'white'
                          }} title="Authorized provisions">
                            {regulation.authorized_count}
                          </span>
                        )}
                        {regulation.unauthorized_count > 0 && (
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: '#ef4444',
                            color: 'white'
                          }} title="Unauthorized provisions">
                            {regulation.unauthorized_count}
                          </span>
                        )}
                        
                        {regulation.discretionary_requirements_total > 0 && (
                          <span style={{
                            padding: '2px 8px',
                            background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#dc2626'
                          }}>
                            {regulation.discretionary_requirements_total} DR
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          // Consolidation mode - render like regular regulations for proper scrolling
          groupsLoading ? (
            <div className="loading" style={{ padding: '40px', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              <div style={{ color: '#6c757d', fontSize: '14px' }}>
                Calculating similar regulation groups...
              </div>
            </div>
          ) : regulationGroups.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px' }}>
              <p className="text-small" style={{ textAlign: 'center', color: '#6c757d' }}>
                No regulation groups found with current threshold.
                <br />
                Try adjusting the similarity threshold or selecting more agencies.
              </p>
            </div>
          ) : (
            <div className="regulation-list">
              {regulationGroups.filter(group => group && group.id).map((group) => {
                const isSelected = selectedGroup === group.id;
                const totalDiscRequirements = (group.regulations || []).reduce(
                  (sum, reg) => sum + (reg.discretionary_requirements_total || 0),
                  0
                );

                return (
                  <div
                    key={group.id}
                    className={`regulation-group-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      if (selectedGroup === group.id) {
                        setSelectedGroup(null);
                        clearSelectedRegulations();
                      } else {
                        setSelectedGroup(group.id);
                        // Clear and select all regulations in the group
                        clearSelectedRegulations();
                        group.regulations.forEach(reg => {
                          selectRegulation(reg.id);
                        });
                      }
                    }}
                >
                  {/* Clean regulation list */}
                  <div className="group-regulations-list">
                    {group.regulations.map((reg, idx) => (
                      <div key={reg.id} className="regulation-list-item">
                        <span className="reg-number">{reg.number}</span>
                        {reg.discretionary_requirements_total > 0 && (
                          <span className="reg-disc-badge">
                            {reg.discretionary_requirements_total} DR
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Summary stats row */}
                  <div className="group-summary">
                    <div className="summary-stats">
                      <span className="total-regs">{group.regulations.length} regs</span>
                      {totalDiscRequirements > 0 && (
                        <>
                          <span className="separator">•</span>
                          <span className="total-disc">{totalDiscRequirements} DR total</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Authorizing statute badge */}
                  {group.sharedStatute && (
                    <div className="statute-badge">
                      <span className="statute-label">Statute:</span>
                      <span className="statute-value">{group.sharedStatute}</span>
                    </div>
                  )}

                  {/* Similarity meter */}
                  <div className="group-similarity">
                    <div className="similarity-bar">
                      <div
                        className="similarity-fill"
                        style={{ width: `${group.averageSimilarity * 100}%` }}
                      />
                    </div>
                    <span className="similarity-label">
                      {Math.round(group.averageSimilarity * 100)}% similar
                    </span>
                  </div>
                </div>
              );
            })}
            </div>
          )
        )}
      </div>
    </aside>
  );
};

export default Sidebar;